import _ from 'lodash';
import {useContext, useEffect, useRef, useState} from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import PrimaryButton from '../../../../ui/PrimaryButton';
import IfmsModal from '../../../../ui/IfmsModal';
import useTableNavigation from '../../../../ui/tables/useTableNavigation';

import ApplicationContext from '../../../../ApplicationContext';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import useFastStorage, {useTypedFastStorage} from '../../../../hooks/useFastStorage';

import {ApplicableTableSettings, codesContainerTypeList} from '../../../../tableSettings';

import {
  NumberedIndicatorRenderer,
  TextAlignLeftRenderer,
  TextRenderer,
} from '../../../../uiControls/SmartTableRenderers';

import SmartTable from '../../../../uiControls/SmartTable';
import SecondaryButton from '../../../../ui/SecondaryButton';
import InputControlRow from '../../../../ui/layout/InputControlRow';
import TextBox from '../../../../ui/TextBox';
import Date from '../../../../ui/Date';
import LeftRightSplit from '../../../../ui/LeftRightSplit';
import Select from '../../../../ui/Select';
import useFunction from '../../../../hooks/useFunction';
import {now} from '../../../../dateUtils';
import CloseButton from '../../../../ui/CloseButton';
import { CodeContainerType } from '../../../../fms.core/CodeContainerType';
import { JobNames } from '../../../../framework.core/JobNames';
import { ApiResult, InsertResultsWithData } from '../../../../framework.core/data/ApiResult';
import useDirtyCheck from '../../../../hooks/useDirtyCheck2';
import { getSQLInfoList } from '../../../../fms.core/data/TrackingModule';
import { findSecurityTask, stringsEqualIgnoreCase } from '../../../../util';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import { Search } from '../../../../filtering';
import { CodeContainerTypeListItem } from '../../../../fms.data/models/CodeContainerTypeListItem';
import GreenButton from '../../../../ui/GreenButton';
import { TrackableEntity } from '../../../../fms.core/data/ChangeTrackable';
import { FmsSecTask } from '../../../../fms.core/constants';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import { Portal } from 'react-portal';

const LengthOptions = [
  { value: '2', label: '20 Feet' },
  { value: '4', label: '40 Feet' },
  { value: 'L', label: '45 Feet' },
  { value: 'M', label: '48 Feet' },
];

const HeightOptions = [
  { value: '2', label: '8 feet 6 inches' },
  { value: '5', label: '9 feet 6 inches "Hight Cube"' },
];

const GroupOptions = [
  { value: '20', label: '20 feet' },
  { value: '40', label: '40 feet' },
  { value: '45', label: '45 feet' },
  { value: 'HQ', label: 'High Cube' },
];

const TypeOptions = [
  { value: 'G0', label: 'General Purpose Container' },
  { value: 'R0', label: 'Refrigerated Container' },
  { value: 'U0', label: 'Open Top Container' },
  { value: 'P0', label: 'Platform Container' },
  { value: 'T0', label: 'Tank Container' },
  { value: 'B0', label: 'Dry Bulk Container' },
];

function RowSpacer() {
  return (
    <div style={{height: '10px'}}>&nbsp;</div>
  );
}

interface IContainerType {
  containerType: string,
  description: string,
  containerGroup: string,
  isoType?: string,
  height: string,
  length: string,
  relatedType: string,
  editable: boolean,
  inactive: boolean,
  u2Date?: string,
  updatedBy?: string,
  inactiveDate?: string,
  inactiveBy?: string,
  // Current operation
  edit: boolean,
  view: boolean,
  //<pt220819: containerType is pkey, should not be changed for update.>
  // When editing, container type does not have id
  //oldType?: string,
}

function calculateIsoSizeAndType(length:string, height:string, type:string) {
  if (!length || !height || !type) {
    return '';
  }

  return `${length}${height}${type}`;
}

function ContainerTypeDetails({
  readonly,
  containerType,
  onChange
}:{
  readonly: boolean,
  containerType: CodeContainerType,
  onChange: (change: Partial<CodeContainerType>) => void
}) {

  return (
    <div>
      <div style={{float: 'left', marginRight: '10px', width: 'calc(50% - 10px)'}}>
        <RowSpacer />

        <InputControlRow label="Container Type *">
          <TextBox textId='code-containerType' maxLength={10} disabled={readonly || containerType.edit} value={containerType?.containerType || ''} onChange={e => onChange({containerType: e.target.value})} />
        </InputControlRow>

        <InputControlRow label="Description">
          <TextBox textId='code-description' maxLength={100} disabled={readonly} value={containerType?.description || ''} onChange={e => onChange({description: e.target.value})} />
        </InputControlRow>

        <InputControlRow label="Group *">
          <LeftRightSplit
            left={
              <div style={{marginRight: '10px'}}>
                <Select
                  maxLength={10}
                  selectId='code-containerGrp'
                  items={GroupOptions}
                  value={containerType?.containerGrp || ''}
                  disabled={readonly}
                  onChange={e => onChange({containerGrp: e.target.value})}
                />
              </div>
            }
            right={<></>}
          />
        </InputControlRow>

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="Update Date">
                  <Date dateId='code-u2Date' disabled value={containerType?.u2Date} />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <InputControlRow label="By">
                <TextBox textId='code-updatedBy' disabled value={containerType?.updatedBy || ''} />
              </InputControlRow>
            </div>
          }
        />
      </div>

      <div style={{float: 'left', marginLeft: '10px', width: 'calc(50% - 10px)'}}>
        <RowSpacer />

        <InputControlRow label="ISO Size & Type">
          <LeftRightSplit
            left={
              <div style={{marginRight: '10px'}}>
                <TextBox textId='code-isoCode' disabled value={containerType?.isoCode} />
              </div>
            }
            right={<></>}
          />
        </InputControlRow>

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="Length">
                <Select
                  maxLength={10}
                  selectId='code-length'
                  items={LengthOptions}
                  value={containerType?.length || ''}
                  disabled={readonly}
                  onChange={e => onChange({length: e.target.value, isoCode: calculateIsoSizeAndType(e.target.value, containerType?.height, containerType?.relatedType)})}
                />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
              <InputControlRow label="Height">
                <Select
                  maxLength={30}
                  selectId='code-height'
                  items={HeightOptions}
                  value={containerType?.height || ''}
                  disabled={readonly}
                  onChange={e => onChange({height: e.target.value, isoCode: calculateIsoSizeAndType(containerType?.length, e.target.value, containerType?.relatedType)})}
                />
              </InputControlRow>
            </div>
          }
        />

        <LeftRightSplit
          left={
            <div style={{marginRight: '10px'}}>
              <InputControlRow label="Type">
                <Select
                  maxLength={30}
                  selectId='code-relatedType'
                  items={TypeOptions}
                  value={containerType?.relatedType || ''}
                  disabled={readonly}
                  onChange={e => onChange({relatedType: e.target.value, isoCode: calculateIsoSizeAndType(containerType?.length, containerType?.height, e.target.value)})}
                  onTabNext={() => {
                    if (containerType.edit) {
                      document.getElementById('code-description')?.focus();
                    }
                    else {
                      document.getElementById('code-containerType')?.focus();
                    }
                    return true;
                  }}
                />
              </InputControlRow>
            </div>
          }
          right={
            <div style={{marginLeft: '10px'}}>
            </div>
          }
        />
      </div>
    </div>
  );
}

function formatElement(data:any, index:number) {
  return {...data, indicator: index + 1};
}

function formatData(data:Array<any>) {
  return data.map((d, i) => formatElement(d, i));
}

function ContainerTypeComponent() {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
  }});
  useFunctionKeys({ onEsc: cancelOperation, onSave: handleSaveAndClose });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const { selectTableRow, tableNavigationCleanup } = useTableNavigation('codes-container-type-list', onRowSelect, onRowDoubleSelect, ['containerType'], false, false, false);

  const tableRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-container-type-list`, []);
  const [containerType, _setContainerType, removeContainerTypeFromStorage, containerTypeRef] = useTypedFastStorage<CodeContainerType>(`ifms-referenceFiles-container-type-editable`, null);
  const { setCleanState, dirtyCleanup } = useDirtyCheck(`ifms-referenceFiles-container-type-editable`, ['edit', 'view']);
  const systemSettings = useSystemSettings();
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeContainerType);
  const [selectedRow, setSelectedRow] = useState(-1);

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
      const modalsWithoutSaveBtn = Array.from(modals).filter(modal => { return !modal.contains(saveBtn); });
      if (modalsWithoutSaveBtn.length <= 0)
        saveBtn.click();
    }
  }

  function setContainerType(c:any, skipDirty:boolean = false) {
    if (!skipDirty) {
      setHasChanges(true);
    }
    _setContainerType(c);
  }

  const tableHeight = 520;

  function setData(d:Array<IContainerType>) {
    removeData();
    _setData(d);
  }

  async function fetchData(api: any) {
    setLoading(true);

    //<pt220819: portal
    //const apiResult = await api.getCodesContainerTypes();

    //setData(formatData(apiResult ?? []));
    const search = { pageSize: systemSettings.defaultPageSize} as Search;
    const apiResult = await api.getListItems(JobNames.Container,
      'CodeContainerTypeListItem',
      CodeContainerTypeListItem.calculateSearchParameters(search),
      1, search.pageSize);
    
      setData(formatData(apiResult && apiResult.entities ? apiResult.entities : []));
    //pt220819>
    setHasChanges(false);
    setLoading(false);
  }

  useEffect(() => {
    async function executeAsyncFunc() {
      if (data.length === 0)
        await reload();
    }
    executeAsyncFunc();
    return () => {
      removeData();
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && loading === false) {
      selectTableRow(0);
      onRowSelect(0);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading]
  );

  async function onRowSelect(rowIndex: number){
    setSelectedRow(!isNaN(rowIndex) ? rowIndex : -1 );
  }


  async function onRowDoubleSelect(rowIndex:number) {
    if (!dataRef || !dataRef.current) {
      return;
    }
    if (rowIndex >= 0 && rowIndex < dataRef.current.length && _.isEmpty(containerType) //&& dataRef.current[rowIndex].editable
    ) {
      //<pt220819: portal
      // setContainerType({
      //   ..._.cloneDeep(dataRef.current[rowIndex]),
      //   edit: true,
      //   view: true,
      //   oldType: dataRef.current[rowIndex].type,
      // },
      // true);
      const apiResult:ApiResult<CodeContainerType> = await appContext.api.getJobDetail(JobNames.Container,
        { containerType: dataRef.current[rowIndex].containerType});
      if(!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        return;
      }
   
      const objContainer = { ...apiResult.result, edit: true, view: true, editIndex: rowIndex};
      setCleanState(objContainer);
      const tEntity = new CodeContainerType(objContainer);
      tEntity.trackable = true;
      setContainerType(tEntity, true);
      //pt220819>
    }
  }

  async function reload() {
    await fetchData(appContext.api);
  }

  function selectContainerTypeForNew() {
    //clearTableSelection();
    
    const newCodeModel = new CodeContainerType();   
    setContainerType(newCodeModel, false);
    setCleanState(newCodeModel);
    
    setContainerType({
      edit : false,
      view: false,
      description: '',
      editable: true,
      containerGrp: GroupOptions[0].value,
      height: HeightOptions[0].value,
      length: LengthOptions[0].value,
      type: '',
      relatedType: TypeOptions[0].value,
      isoCode: calculateIsoSizeAndType(LengthOptions[0].value, HeightOptions[0].value, TypeOptions[0].value)
      //inactive: false
    });

    if (containerTypeRef.current) {
      containerTypeRef.current.trackable = true;
    }
  }

  function cancelOperation() {
    setHasChanges(false);

    //clearTableSelection();
    removeContainerTypeFromStorage();
    return true;
  }

  async function save() {
    if (!containerType) {
      return;
    }

    if (!containerType.containerType) {
      await showGlobalOkDialog(<p>Please check following information. (Specify the missing entries.)
        <br />
        * Must Enter Container Type
      </p>);
      return;
    }

    interface CntrCheckParam {
      u2Date: string,  // org u2Date in ISO format
      containerType: string
    }

    const CntrCheckData:CntrCheckParam = {
      u2Date: containerType.u2Date ?? '',
      containerType : containerType.containerType
    };

    const checkData:CntrCheckParam = await appContext.api.chkdupContainer(
      containerType.containerType, CntrCheckData
    );

    if (checkData && checkData.containerType === "error" && !containerType.edit) {
      await showGlobalOkDialog(<p>  * Duplicate Container Type *</p>);
      return;
    }
    
    const mainChange:Partial<CodeContainerType> = {};
    mainChange.u2Date = now();
    mainChange.updatedBy = appContext.user?.id ?? '';
    setContainerType(mainChange)

    // const tEntity = new CodeContainerType(containerType);
    // tEntity.trackable = true;
    // tEntity.u2Date = now();
    // tEntity.updatedBy = appContext.user?.id ?? '';
    // setContainerType(tEntity, false);

    if(!containerTypeRef.current?.isDirty) {
      cancelOperation();
      await reload();
      return;
    }

    const sqlInfoList = getSQLInfoList(containerType as TrackableEntity);
    // update @ConType of t_aotherinfo
    const sqlInfo = sqlInfoList.find(x => stringsEqualIgnoreCase(x.tableName, 't_aotherinfo'));
    if(sqlInfo) {
      sqlInfo.whereFields.forEach(f => {
        if(f.value === '@ConType')
          f.value = containerType.containerType;
      });
    }

    const apiResult:ApiResult<InsertResultsWithData> = await appContext.api.saveJobDetail(
      JobNames.Container,
      sqlInfoList
    );

    if(!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }

    await showGlobalOkDialog(<p>The data has been saved</p>);
    
    cancelOperation();
    dirtyCleanup();
    await reload();
    //pt220819>
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-container-type-list',
    ref: tableRef,
    selectable: true,
    tableName: 'codesContainerTypeList',
    ...codesContainerTypeList,
    height: `${tableHeight}px`,
    data: (data ?? []).map((x:any) => ({...x})),
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'containerType', Cell: TextAlignLeftRenderer },
      { accessor: 'description', Cell: TextAlignLeftRenderer },
      { accessor: 'containerGroup', Cell: TextRenderer },
      { accessor: 'isoType', Cell: TextRenderer },
      // { accessor: 'length', Cell: TextRenderer },
      // { accessor: 'height', Cell: TextRenderer },
      // { accessor: 'relatedType', Cell: TextRenderer },
      // { accessor: 'inactive', Cell: NullableCheckboxIconRenderer },
      // { accessor: 'inactiveDate', Cell: DateRenderer },
      // { accessor: 'u2Date', Cell: DateTimeRenderer },
    ],
  };

  //const newEnabled = containerType === null;
  const hasSelectedRow = !isNaN(selectedRow) && !(selectedRow < 0);
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;
  
  async function selectContainerTypeForView() {
    //clearTableSelection();
    await setContainerTypeDetail(true);
  }

  async function selectContainerTypeForEdit() {
    //clearTableSelection();
    await setContainerTypeDetail(false);
  }

  async function selectContainerTypeForDelete() {
    const doConfirm = await showGlobalYesNoDialog(
      <div>
        <h3>Confirm to Delete?</h3>
      </div>
    , false, 0, true, 'warning');
    if (doConfirm) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.Container, { id: dataRef.current[selectedRow].containerType, pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        await reload();
      } else
        showGlobalOkDialog(<p>{"Cannot delete"}</p>);
    } else
      return;
  }

  async function setContainerTypeDetail(isView: boolean) {
    if (selectedRow >= 0 && selectedRow < dataRef.current.length && _.isEmpty(containerType)) {
      const apiResult: ApiResult<CodeContainerType> = await appContext.api.getJobDetail(JobNames.Container,
        { containerType: dataRef.current[selectedRow].containerType });
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        return;
      }
      const objContainer = { ...apiResult.result, edit: true, view: isView, editIndex: selectedRow };
      setCleanState(objContainer);
      const tEntity = new CodeContainerType(objContainer);
      tEntity.trackable = true;
      setContainerType(tEntity, true);
    }
  }

  const containerCount = data ? data.length : 0;
  return (
    <div style={{ marginTop: '40px' }}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Ocean <FontAwesomeIcon icon={faAngleRight} />&nbsp;Container Type</h1>
      </Portal> */}

      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', marginTop:'5px'}}>
          <div className="ifms-common-bar-element" style={{marginLeft:'66px'}}>
            <CloseButton name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '600px' }}>
        <div style={{ marginLeft: '10px', fontSize: '11px', fontWeight: 'bold' }}>
          ** Total Number Container Size: {containerCount} **
        </div>

        <div style={{ marginLeft: 'auto' }}></div>

        <div style={{ position: 'relative', top: '-4px' }}>
          <SecondaryButton name='codes-container-type-add-button' size="regular" disabled={!canBeAdd} onClick={selectContainerTypeForNew}>New</SecondaryButton>&nbsp;&nbsp;
          <SecondaryButton name='codes-container-type-view-button' size="regular" disabled={!hasSelectedRow} onClick={selectContainerTypeForView}>View</SecondaryButton>&nbsp;&nbsp;
          <SecondaryButton name='codes-container-type-edit-button' size="regular" disabled={!canBeEdit} onClick={selectContainerTypeForEdit}>Edit</SecondaryButton>&nbsp;&nbsp;
          <SecondaryButton name='codes-container-type-delete-button' size="regular" disabled={!canBeDeleted} onClick={selectContainerTypeForDelete}>Delete</SecondaryButton>&nbsp;
          <BlackInverseButton name='list-screen-refresh' size="large" onClick={async () => { await reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>
      <div style={{ width: '600px' }}>
        <SmartTable {...tableOptions} />
      </div>

      <RowSpacer />

      <IfmsModal
        width="800px"
        isOpen={!!containerType}
        title={containerType && containerType.edit ? (containerType.view ? 'Container Type View' : 'Container Type Edit') : 'Container Type New'}
        onCancel={cancelOperation}
      >
        {!!containerType &&
          <div>
            <div style={{ display: 'flex' }}>
              <div style={{ marginLeft: 'auto' }}></div>
              <div className="ifms-common-bar-element ifms-right-separator-light-gray">
                {containerType.view && listSec?.grantEdit &&
                  <PrimaryButton name='container-type-detail-edit-button' size="regular" onClick={() => setContainerType({ view: false }, true)}>Edit</PrimaryButton>
                }
                {!containerType.view &&
                  <GreenButton name='container-type-detail-save-button' size="regular" onClick={save}>Save</GreenButton>
                }
                &nbsp;
              </div>&nbsp;&nbsp;
              <div className="ifms-common-bar-element">
                <CloseButton name='container-type-detail-close-button' onClick={cancelOperation} />
              </div>
            </div>
            <ContainerTypeDetails readonly={containerType.view} containerType={containerType} onChange={setContainerType} />
            <div style={{ clear: 'both' }}></div>
          </div>
        }
      </IfmsModal>
    </div>
  );
}

export default ContainerTypeComponent;
